import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { LayoutList } from 'lucide-react'
import Modal from './ui/Modal'
import AscapMark from './AscapMark'
import { nvAddColumnRequiredHeading, nvPillButtonClass } from '../lib/newViewModalUi'
import {
  makeAscapImportSessionStorageKey,
  parseAscapWorksCatalogCsv,
} from '../lib/ascapWorksCatalogImport'

const LOGO_BASE = '/logos/data-upload'

type PartnerId =
  | 'uta'
  | 'caa'
  | 'wme'
  | 'the-team'
  | 'ticketmaster'
  | 'axs'
  | 'eventbrite'
  | 'ascap'
  | 'bmi'
  | 'sesac'
  | 'socan'

const PARTNER_LABEL: Record<PartnerId, string> = {
  uta: 'UTA',
  caa: 'CAA',
  wme: 'WME',
  'the-team': 'The Team',
  ticketmaster: 'Ticketmaster',
  axs: 'AXS',
  eventbrite: 'Eventbrite',
  ascap: 'ASCAP',
  bmi: 'BMI',
  sesac: 'SESAC',
  socan: 'SOCAN',
}

const PARTNER_FILE: Record<PartnerId, string> = {
  uta: 'uta.svg',
  caa: 'caa.svg',
  wme: 'wme.svg',
  'the-team': 'the-team.svg',
  ticketmaster: 'ticketmaster.svg',
  axs: 'axs.svg',
  eventbrite: 'eventbrite.svg',
  ascap: 'ascap.svg',
  bmi: 'bmi.svg',
  sesac: 'sesac.svg',
  socan: 'socan.svg',
}

/** 30% smaller than default slot (0.7 × max-h-8 / max-w) */
const COMPACT_LOGO_IDS = new Set<PartnerId>(['caa', 'axs'])

/** Slightly larger slot (wide viewBox wordmark reads small at default) */
const EXPANDED_LOGO_IDS = new Set<PartnerId>(['wme'])

/** Dark-mode: raster/SVG-as-img → white via invert (SOCAN / ASCAP use inline marks for brand colors). */
const DARK_WHITE_FILTER_IDS = new Set<PartnerId>(['ticketmaster', 'wme'])

/** SOCAN: black “S”/“AN” → white in dark; middle path stays brand red #D02139. */
function SocanMark({ className = '' }: { className?: string }) {
  return (
    <span
      className={`inline-flex h-8 w-[4.5rem] shrink-0 items-center justify-center overflow-visible dark:drop-shadow-[0_0.5px_0_rgba(0,0,0,0.5)] ${className}`}
    >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 116 25"
      fill="none"
      fillRule="evenodd"
      className="block h-full w-full max-h-8 max-w-full"
      preserveAspectRatio="xMidYMid meet"
      aria-hidden
      focusable="false"
    >
      <path
        className="fill-gray-900 dark:fill-white"
        d="M2.68 19.238c1.153 1.73 3.155 2.53 5.088 2.53 2.137 0 4.782-1.332 4.782-3.928C12.55 12.415.713 15.776.713 6.59.713 3.828 3.087 0 8.853 0c2.612 0 5.122.533 6.92 2.697L13.127 5.06c-.815-1.232-2.41-2.065-4.275-2.065-3.595 0-4.68 2.13-4.68 3.595 0 5.958 11.838 2.397 11.838 10.883 0 4.56-3.732 7.289-8.344 7.289C4.58 24.762 1.629 23.73 0 21.5l2.68-2.262M39.701 12.381c0-5.158-3.505-9.386-8.91-9.386-5.409 0-8.913 4.228-8.913 9.386 0 5.159 3.504 9.386 8.912 9.386 5.406 0 8.911-4.227 8.911-9.386Zm-21.227 0C18.474 5.392 23.58 0 30.79 0 38 0 43.105 5.392 43.105 12.381c0 6.99-5.106 12.38-12.315 12.38-7.21 0-12.316-5.39-12.316-12.38Z"
      />
      <path fill="#D02139" d="M63.358 5.691c-1.325-1.73-3.512-2.696-5.665-2.696-5.268 0-8.745 4.228-8.745 9.586 0 4.959 3.278 9.186 8.745 9.186 2.684 0 4.737-1.198 6.129-3.162l2.683 2.03c-.696.965-3.512 4.127-8.878 4.127-7.454 0-12.059-6.257-12.059-12.18C45.568 5.258 50.571 0 57.792 0c3.081 0 6.327 1.164 8.216 3.661l-2.65 2.03" />
      <path
        className="fill-gray-900 dark:fill-white"
        d="m83.87 16.157-4.4-10.799h-.066l-4.467 10.799h8.934ZM78.137 1.238h2.9l10.1 23.524h-3.732l-2.367-5.815H73.803l-2.333 5.815h-3.733l10.4-23.524ZM94.832 1.238h4.296l13.299 19.337h.066V1.238h3.275v23.524h-4.16L98.173 5.424h-.068v19.338h-3.273V1.238"
      />
    </svg>
    </span>
  )
}

/** Modest bump over default 18px; compact cell. */
function TheTeamWordmark({ className = '' }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 200 40"
      fill="none"
      preserveAspectRatio="xMidYMid meet"
      className={`block h-[1.875rem] w-auto max-w-none shrink-0 text-gray-900 dark:text-white ${className}`}
      aria-hidden
    >
      <text
        x="100"
        y="26"
        textAnchor="middle"
        fill="currentColor"
        style={{ fontFamily: 'system-ui, -apple-system, Segoe UI, sans-serif' }}
        fontSize="18"
        fontWeight="700"
        letterSpacing="0.04em"
      >
        THE TEAM
      </text>
    </svg>
  )
}

function PartnerList({ ids }: { ids: PartnerId[] }) {
  return (
    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
      {ids.map((id) => PARTNER_LABEL[id]).join(', ')}
    </span>
  )
}

/**
 * Uniform logo slots: fixed outer box so wide/tall marks (CAA, AXS, BMI) don’t dominate.
 * Light mode: Tailwind gray-200 (reads as a soft gray on white cards).
 * Dark mode: mid charcoal aligned with app tokens — Tailwind gray-200 is ~#e5e7eb and reads “white” on
 * #13120A; use a raised surface tint so boxes look gray, not a light panel.
 */
function partnerLogoSlotClass(id: PartnerId) {
  return `flex flex-shrink-0 items-center justify-center rounded-md border px-2 py-1.5 shadow-sm transition-colors ${
    id === 'the-team'
      ? 'h-11 w-[6.25rem]'
      : EXPANDED_LOGO_IDS.has(id)
        ? 'h-12 w-[7.25rem]'
        : 'h-11 w-[5.75rem]'
  }`
}

function PartnerLogoMark({ id }: { id: PartnerId }) {
  return id === 'the-team' ? (
    <TheTeamWordmark className="dark:drop-shadow-[0_0.5px_0_rgba(0,0,0,0.5)]" />
  ) : id === 'socan' ? (
    <SocanMark />
  ) : id === 'ascap' ? (
    <AscapMark />
  ) : (
    <img
      src={`${LOGO_BASE}/${PARTNER_FILE[id]}`}
      alt=""
      className={`w-auto object-contain object-center dark:drop-shadow-[0_0.5px_0_rgba(0,0,0,0.5)] ${
        COMPACT_LOGO_IDS.has(id)
          ? 'max-h-[1.55rem] max-w-[3.5rem] min-h-[1.25rem] min-w-[2.75rem]'
          : EXPANDED_LOGO_IDS.has(id)
            ? 'max-h-11 max-w-[6.5rem]'
            : 'max-h-8 max-w-[4.5rem]'
      } ${DARK_WHITE_FILTER_IDS.has(id) ? 'dark:brightness-0 dark:invert' : ''}`}
    />
  )
}

/** Decorative row (hub cards): logos are not interactive. */
function PartnerLogoRow({ ids, className = '' }: { ids: PartnerId[]; className?: string }) {
  return (
    <div className={`flex min-w-0 w-full max-w-full flex-wrap items-center justify-start gap-2 ${className}`} aria-hidden>
      {ids.map((id) => (
        <div
          key={id}
          className={`${partnerLogoSlotClass(id)} border-gray-300/90 bg-gray-200 dark:border-[rgb(var(--border-rgb))] dark:bg-[rgb(52,51,42)] dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]`}
          title={PARTNER_LABEL[id]}
        >
          <PartnerLogoMark id={id} />
        </div>
      ))}
    </div>
  )
}

/** One tap: each logo is a button (PRO picker, live shows, ticket counts). */
function PartnerLogoPickRow({
  ids,
  className = '',
  onPick,
  'aria-label': ariaLabel,
}: {
  ids: PartnerId[]
  className?: string
  onPick: (id: PartnerId) => void
  'aria-label'?: string
}) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={`flex min-w-0 w-full max-w-full flex-wrap items-center justify-start gap-2 ${className}`}
    >
      {ids.map((id) => (
        <button
          key={id}
          type="button"
          onClick={() => onPick(id)}
          title={PARTNER_LABEL[id]}
          aria-label={`${PARTNER_LABEL[id]} — upload`}
          className={`${partnerLogoSlotClass(id)} cursor-pointer border-gray-300/90 bg-gray-200 hover:border-gray-400 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 dark:border-[rgb(var(--border-rgb))] dark:bg-[rgb(52,51,42)] dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] dark:hover:border-gray-500 dark:hover:bg-[rgb(58,57,48)] dark:focus-visible:ring-white dark:focus-visible:ring-offset-[rgb(var(--surface-rgb))]`}
        >
          <PartnerLogoMark id={id} />
        </button>
      ))}
    </div>
  )
}

type Flow = 'root' | 'shows-hub' | 'live-shows' | 'ticket-counts' | 'pro'

export type UploadDataModalVariant = 'full' | 'music' | 'shows'

type Props = {
  isOpen: boolean
  onClose: () => void
  /**
   * `full` — dashboard hub (shows + music).
   * `music` — PRO / performance rights statement upload only.
   * `shows` — live show roster + ticket counts only (same paths as dashboard, scoped to Shows).
   */
  variant?: UploadDataModalVariant
}

export default function UploadDataModal({ isOpen, onClose, variant = 'full' }: Props) {
  const navigate = useNavigate()
  const [flow, setFlow] = useState<Flow>('root')
  const proInputRef = useRef<HTMLInputElement>(null)
  /** PRO: set synchronously before `input.click()` so `onChange` sees the chosen society without waiting on React state. */
  const pendingProSourceRef = useRef<PartnerId | null>(null)

  useEffect(() => {
    if (!isOpen) return
    if (variant === 'music') {
      setFlow('pro')
    } else if (variant === 'shows') {
      setFlow('shows-hub')
    } else {
      setFlow('root')
    }
    pendingProSourceRef.current = null
  }, [isOpen, variant])

  const resetAndClose = () => {
    setFlow('root')
    pendingProSourceRef.current = null
    onClose()
  }

  const goShows = (payload: {
    addView: 'csv' | 'ticket-counter'
    ticketImportMode?: boolean
    documentSource: string
    uploadKind: 'live-shows' | 'ticket-counts'
  }) => {
    try {
      sessionStorage.setItem('dizeeUploadDocumentSource', payload.documentSource)
      sessionStorage.setItem('dizeeUploadKind', payload.uploadKind)
    } catch {
      /* ignore */
    }
    navigate('/shows', {
      state: {
        openShowUpload: true,
        addView: payload.addView,
        ticketImportMode: payload.ticketImportMode ?? false,
        documentSource: payload.documentSource,
        uploadKind: payload.uploadKind,
      },
    })
    resetAndClose()
  }

  const liveAgencies: PartnerId[] = ['uta', 'caa', 'wme', 'the-team']
  const ticketDocTypes: PartnerId[] = ['uta', 'caa', 'wme', 'the-team', 'ticketmaster', 'axs', 'eventbrite']
  const proTypes: PartnerId[] = ['ascap', 'bmi', 'sesac', 'socan']

  const title =
    flow === 'root' || flow === 'shows-hub'
      ? 'Upload Data'
      : flow === 'live-shows'
        ? 'Live Shows Upload'
        : flow === 'ticket-counts'
          ? 'Ticket Counts Upload'
          : 'PRO Statement Upload'

  const description =
    flow === 'root' || flow === 'shows-hub'
      ? variant === 'shows'
        ? 'Import a live show roster or ticket sales data.'
        : 'Choose the type of data you are uploading.'
      : flow === 'live-shows'
        ? 'Tap your agency — we’ll open Shows so you can drop your roster file.'
        : flow === 'ticket-counts'
          ? 'Tap the source your report came from — we’ll open ticket import on Shows.'
          : 'Tap your PRO — then choose your statement file (PDF, CSV, or Excel).'

  const backToShowsLanding = () => {
    setFlow(variant === 'shows' ? 'shows-hub' : 'root')
  }

  const showUploadBackPill =
    flow === 'live-shows' ||
    flow === 'ticket-counts' ||
    (flow === 'pro' && (variant === 'full' || variant === 'shows' || variant === 'music'))

  return (
    <Modal
      isOpen={isOpen}
      onClose={resetAndClose}
      title={title}
      description={description}
      maxWidthClassName="max-w-4xl"
      mobileCentered
      chrome="newView"
      newViewIntrinsicHeight
      newViewPanelClassName={
        flow === 'live-shows' || flow === 'ticket-counts' || flow === 'pro'
          ? '!max-w-2xl sm:!max-w-3xl'
          : '!max-w-3xl lg:!max-w-4xl'
      }
      headerExtra={
        showUploadBackPill ? (
          <button
            type="button"
            onClick={() => {
              if (flow === 'live-shows' || flow === 'ticket-counts') {
                backToShowsLanding()
                return
              }
              if (flow === 'pro') {
                if (variant === 'music') {
                  resetAndClose()
                } else {
                  setFlow(variant === 'shows' ? 'shows-hub' : 'root')
                  pendingProSourceRef.current = null
                }
              }
            }}
            className={nvPillButtonClass}
          >
            <LayoutList className="w-3.5 h-3.5 shrink-0" />
            {flow === 'pro' && variant === 'music' ? 'Close' : 'Back'}
          </button>
        ) : null
      }
    >
      {flow === 'root' || flow === 'shows-hub' ? (
        <div className="space-y-8">
          {/* ── Shows ── */}
          <div>
            <h3 className={nvAddColumnRequiredHeading}>Shows</h3>
            <div className="space-y-3">
              <button
                type="button"
                onClick={() => setFlow('live-shows')}
                className="w-full text-left rounded-xl border border-white/[0.08] bg-white/[0.02] hover:bg-white/[0.06] hover:border-white/[0.14] p-4 transition-colors"
              >
                <div className="text-sm font-medium text-gray-900 dark:text-white">
                  Live Shows (Upcoming &amp; Past)
                </div>
                <p className="text-xs text-gray-500 dark:text-zinc-400 mt-1">
                  Upload live show data from your booking agency — dates, venues, guarantees, bonuses, set times, etc.
                </p>
                <div className="flex flex-wrap items-baseline gap-x-1.5 mt-2">
                  <span className="text-xs text-gray-500 dark:text-zinc-500">Optimized for</span>
                  <PartnerList ids={liveAgencies} />
                </div>
                <PartnerLogoRow ids={liveAgencies} className="mt-3 pointer-events-none" />
                <p className="text-[11px] text-gray-400 dark:text-zinc-500 mt-2">
                  Recommended for artists, managers, agents &amp; touring teams
                </p>
              </button>

              <button
                type="button"
                onClick={() => setFlow('ticket-counts')}
                className="w-full text-left rounded-xl border border-white/[0.08] bg-white/[0.02] hover:bg-white/[0.06] hover:border-white/[0.14] p-4 transition-colors"
              >
                <div className="text-sm font-medium text-gray-900 dark:text-white">
                  Ticket Counts (Upcoming &amp; Past)
                </div>
                <p className="text-xs text-gray-500 dark:text-zinc-400 mt-1">
                  Upload daily ticket sales reports from booking agencies and ticketing platforms.
                </p>
                <div className="flex flex-wrap items-baseline gap-x-1.5 mt-2">
                  <span className="text-xs text-gray-500 dark:text-zinc-500">Optimized for</span>
                  <PartnerList ids={['uta', 'caa', 'wme', 'the-team', 'ticketmaster', 'axs', 'eventbrite']} />
                </div>
                <PartnerLogoRow
                  ids={['uta', 'caa', 'wme', 'the-team', 'ticketmaster', 'axs', 'eventbrite']}
                  className="mt-3 pointer-events-none"
                />
                <p className="text-[11px] text-gray-400 dark:text-zinc-500 mt-2">
                  Recommended for artists, managers, agents, promoters &amp; touring teams
                </p>
              </button>
            </div>
          </div>

          {/* ── Music (dashboard hub only) ── */}
          {flow === 'root' ? (
            <div>
              <h3 className={nvAddColumnRequiredHeading}>Music</h3>
              <button
                type="button"
                onClick={() => setFlow('pro')}
                className="w-full text-left rounded-xl border border-white/[0.08] bg-white/[0.02] hover:bg-white/[0.06] hover:border-white/[0.14] p-4 transition-colors"
              >
                <div className="text-sm font-medium text-gray-900 dark:text-white">
                  Performance Rights Data (PROs)
                </div>
                <p className="text-xs text-gray-500 dark:text-zinc-400 mt-1">
                  Upload PRO statements to fill in writer credits, song splits, publishing info &amp; other metadata.
                </p>
                <div className="flex flex-wrap items-baseline gap-x-1.5 mt-2">
                  <span className="text-xs text-gray-500 dark:text-zinc-500">Supported</span>
                  <PartnerList ids={proTypes} />
                </div>
                <PartnerLogoRow ids={proTypes} className="mt-3 pointer-events-none" />
                <p className="text-[11px] text-gray-400 dark:text-zinc-500 mt-2">
                  Recommended for artists, managers, labels &amp; publishers
                </p>
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      {flow === 'live-shows' ? (
        <div>
          <PartnerLogoPickRow
            ids={liveAgencies}
            aria-label="Booking agency — open live show file import"
            onPick={(id) => goShows({ addView: 'csv', documentSource: id, uploadKind: 'live-shows' })}
          />
        </div>
      ) : null}

      {flow === 'ticket-counts' ? (
        <div>
          <PartnerLogoPickRow
            ids={ticketDocTypes}
            aria-label="Ticket report source — open ticket import"
            onPick={(id) =>
              goShows({
                addView: 'ticket-counter',
                ticketImportMode: true,
                documentSource: id,
                uploadKind: 'ticket-counts',
              })
            }
          />
        </div>
      ) : null}

      {flow === 'pro' ? (
        <div className="space-y-4">
          <input
            ref={proInputRef}
            type="file"
            accept=".pdf,.csv,.xlsx,.xls"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0]
              const src = pendingProSourceRef.current
              pendingProSourceRef.current = null
              e.target.value = ''
              if (!f) return
              if (!src) {
                alert('Pick your PRO (ASCAP, BMI, …) first, then choose the file again.')
                return
              }

              const goMoney = () => {
                navigate('/money', {
                  state: { openMoneyAdd: true, proStatementSource: src, proStatementFileName: f.name },
                })
                resetAndClose()
              }

              if (src === 'ascap' && /\.csv$/i.test(f.name)) {
                const reader = new FileReader()
                reader.onload = () => {
                  const text = String(reader.result ?? '')
                  const { payloads, errors } = parseAscapWorksCatalogCsv(text)
                  if (payloads.length > 0) {
                    const sessionKey = makeAscapImportSessionStorageKey()
                    try {
                      sessionStorage.setItem(sessionKey, JSON.stringify(payloads))
                    } catch (err) {
                      console.error(err)
                      alert('Could not store import (browser storage may be full). Try a smaller ASCAP export.')
                      return
                    }
                    navigate('/music', {
                      state: {
                        openAscapWorksImport: true,
                        ascapImportSessionKey: sessionKey,
                        ascapImportMeta: { count: payloads.length, fileName: f.name },
                      },
                    })
                    resetAndClose()
                    return
                  }
                  if (errors.length) {
                    alert(errors.slice(0, 5).join('\n'))
                  } else {
                    alert(
                      'No works found in this CSV. For Music, use your ASCAP “Works Catalog” export. Royalty statements stay on the Money page as manual entries.',
                    )
                  }
                }
                reader.onerror = () => alert('Could not read the file.')
                reader.readAsText(f)
                return
              }

              goMoney()
            }}
          />
          <PartnerLogoPickRow
            ids={proTypes}
            aria-label="Performance rights organization — choose statement file"
            onPick={(id) => {
              pendingProSourceRef.current = id
              proInputRef.current?.click()
            }}
          />
          <p className="text-xs text-gray-400 dark:text-zinc-500">
            ASCAP Works Catalog (.csv) imports into Music. PDF / Excel opens Money to log a statement.
          </p>
        </div>
      ) : null}
    </Modal>
  )
}
